/**
 * AuditLogger - Client-side audit logging for authentication events
 * Tracks login, logout, OTP, and other security events
 */

import apiClient from '../../services/api';

export interface AuditLogEntry {
  action: string;
  status: 'success' | 'failure';
  details?: Record<string, any>;
  timestamp?: string;
  userAgent?: string;
  ipAddress?: string;
}

class AuditLogger {
  /**
   * Log authentication event
   */
  async logAuthEvent(
    action: 'login' | 'logout' | 'otp_send' | 'otp_verify' | 'register' | 'token_refresh',
    status: 'success' | 'failure',
    details?: Record<string, any>
  ): Promise<void> {
    try {
      const entry: AuditLogEntry = {
        action,
        status,
        details: {
          ...details,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent
        }
      };

      // Send to backend for persistent logging
      await apiClient.post('/audit/log', entry).catch(() => {
        // Fail silently - don't interrupt user flow if logging fails
        console.warn('Failed to log audit event:', action);
      });

      // Also log locally for debugging
      this.logLocal(entry);
    } catch (error) {
      console.error('Audit logging error:', error);
    }
  }

  /**
   * Log login attempt
   */
  async logLogin(
    email: string,
    success: boolean,
    reason?: string
  ): Promise<void> {
    await this.logAuthEvent('login', success ? 'success' : 'failure', {
      email: this.maskEmail(email),
      reason
    });
  }

  /**
   * Log logout
   */
  async logLogout(): Promise<void> {
    await this.logAuthEvent('logout', 'success');
  }

  /**
   * Log OTP send
   */
  async logOTPSend(phone: string, success: boolean): Promise<void> {
    await this.logAuthEvent('otp_send', success ? 'success' : 'failure', {
      phone: this.maskPhone(phone)
    });
  }

  /**
   * Log OTP verification
   */
  async logOTPVerify(phone: string, success: boolean): Promise<void> {
    await this.logAuthEvent('otp_verify', success ? 'success' : 'failure', {
      phone: this.maskPhone(phone)
    });
  }

  /**
   * Log registration
   */
  async logRegistration(email: string, success: boolean): Promise<void> {
    await this.logAuthEvent('register', success ? 'success' : 'failure', {
      email: this.maskEmail(email)
    });
  }

  /**
   * Log token refresh
   */
  async logTokenRefresh(success: boolean): Promise<void> {
    await this.logAuthEvent('token_refresh', success ? 'success' : 'failure');
  }

  /**
   * Get login history
   */
  async getLoginHistory(limit: number = 10): Promise<AuditLogEntry[]> {
    try {
      const response = await apiClient.get<AuditLogEntry[]>(
        `/audit/login-history?limit=${limit}`
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch login history:', error);
      return [];
    }
  }

  /**
   * Mask email for privacy
   */
  private maskEmail(email: string): string {
    const [local, domain] = email.split('@');
    const masked = local.substring(0, 2) + '*'.repeat(Math.max(0, local.length - 2)) + '@' + domain;
    return masked;
  }

  /**
   * Mask phone for privacy
   */
  private maskPhone(phone: string): string {
    return phone.substring(0, 2) + '*'.repeat(Math.max(0, phone.length - 4)) + phone.substring(phone.length - 2);
  }

  /**
   * Log locally for debugging
   */
  private logLocal(entry: AuditLogEntry): void {
    const timestamp = new Date().toISOString();
    console.log(`[AUDIT] ${timestamp} - ${entry.action} (${entry.status})`, entry.details);
  }
}

const auditLogger = new AuditLogger();
export default auditLogger;
